// BO'S CADENCE — the one file that knows the 7-touch ladder and the wait gate.
//
// Ported from the openclaw sales-tracker's sales_state.py, which is the
// canonical implementation of Bo's follow-up arithmetic ("the wait gate,
// replies, and exclusions are already computed in code"). Both the follow-up
// operation (lib/agentRun.js) and the draft provenance (lib/draftProvenance.js)
// read THIS file, so the engine and the story it tells can never disagree
// about what "due" means.
//
// The rules, verbatim from the source system:
//   - The ladder: Email Seq1 → Seq2 → Seq3 → Phone call → Email Seq4 → Seq5 →
//     Phone call. Touch number = deduped outbound touches + 1, capped at 7;
//     past 7 is "cadence complete" (nurture), not an eighth email.
//   - THE WAIT GATE: a lead is due only when >= 3 BUSINESS days
//     (Mon–Fri, America/Chicago) have passed since the last outbound touch.
//     Never inside the window.
//   - Reply freeze: a real inbound after our last outbound pulls the lead out
//     of the cadence entirely — a human replies, the machine never does.
//     Auto-replies (OOO, calendar responses) do not count as replies.

export const WAIT_BUSINESS_DAYS = 3

// Touch 1 is index 0. isCallTouch(n) is the only place the call/email split is
// decided, so the engine and the UI can't disagree about which touches ring.
export const CADENCE_TOUCHES = [
  'Email Seq1',
  'Email Seq2',
  'Email Seq3',
  'Phone call',
  'Email Seq4',
  'Email Seq5',
  'Phone call',
]

export const CADENCE_LINE = CADENCE_TOUCHES.join(' → ')

export function touchLabel(n) {
  if (!Number.isFinite(n) || n < 1) return ''
  if (n > CADENCE_TOUCHES.length) return 'Cadence complete'
  return CADENCE_TOUCHES[n - 1]
}

export function isCallTouch(n) {
  return touchLabel(n) === 'Phone call'
}

export function isEmailTouch(n) {
  const label = touchLabel(n)
  return Boolean(label) && label !== 'Phone call' && label !== 'Cadence complete'
}

// Same shapes sales_state.py refuses to count as a human reply.
export const AUTOREPLY_RE =
  /^(automatic reply|auto[- ]?reply|out of office|ooo:|accepted:|declined:|tentative:|invitation:)/i

// ── business-day arithmetic, America/Chicago ────────────────────────────────
//
// Bo works Central Time, so "3 business days" is counted on his calendar, not
// the browser's. Intl gives us the weekday and date in that zone without
// dragging in a timezone library.

const CHI_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  weekday: 'short',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

// { y, m, d, weekday } of a Date, as it reads on the Chicago calendar.
function chicagoParts(date) {
  const parts = {}
  for (const p of CHI_FMT.formatToParts(date)) parts[p.type] = p.value
  return {
    y: Number(parts.year),
    m: Number(parts.month),
    d: Number(parts.day),
    weekday: parts.weekday,
  }
}

const isWeekend = (w) => w === 'Sat' || w === 'Sun'
const DAY_MS = 24 * 60 * 60 * 1000

// A date-only key that two Dates share iff they fall on the same Chicago day.
const dayKey = ({ y, m, d }) => y * 10000 + m * 100 + d

/**
 * Whole business days (Mon–Fri, America/Chicago) elapsed since `from`,
 * counting calendar days between the two Chicago dates and skipping weekends —
 * the same walk sales_state.py does. Returns null for an unreadable date.
 */
export function businessDaysSince(from, now = new Date()) {
  const start = from instanceof Date ? from : new Date(from)
  if (Number.isNaN(start.getTime())) return null
  let t = start.getTime()
  const end = dayKey(chicagoParts(now))
  let count = 0
  // Walk forward a day at a time on the Chicago calendar. Bounded: anything
  // more than ~2 years back just reports "far past the gate".
  for (let i = 0; i < 800; i++) {
    t += DAY_MS
    const parts = chicagoParts(new Date(t))
    if (dayKey(parts) > end) break
    if (!isWeekend(parts.weekday)) count++
    if (dayKey(parts) === end) break
  }
  return count
}

/**
 * The Chicago date `n` business days after `from` — the day a waiting lead
 * becomes due. Returns a Date pinned to noon UTC of that calendar day (a
 * display value, not an instant), or null for an unreadable input.
 */
export function addBusinessDays(from, n = WAIT_BUSINESS_DAYS) {
  const start = from instanceof Date ? from : new Date(from)
  if (Number.isNaN(start.getTime())) return null
  let t = start.getTime()
  let added = 0
  for (let i = 0; i < 800 && added < n; i++) {
    t += DAY_MS
    if (!isWeekend(chicagoParts(new Date(t)).weekday)) added++
  }
  const p = chicagoParts(new Date(t))
  return new Date(Date.UTC(p.y, p.m - 1, p.d, 12, 0, 0))
}

/**
 * The wait gate itself. Due = at least WAIT_BUSINESS_DAYS business days since
 * the last touch. A lead with NO prior touch is due by definition (touch 1 —
 * though the follow-up operation deals only in leads with history).
 *
 * A lastTouchAt that EXISTS but cannot be read is NOT due: the gate cannot be
 * computed, and this module never guesses. Callers that want to surface such
 * leads must check businessDaysSince() === null themselves (the engine's
 * judgeWaitGate does, and reports them as "left out rather than guessed").
 */
export function waitGatePassed(lastTouchAt, now = new Date()) {
  if (!lastTouchAt) return true
  const bd = businessDaysSince(lastTouchAt, now)
  return bd !== null && bd >= WAIT_BUSINESS_DAYS
}
